import React, { Component } from "react";
import PropTypes from "prop-types";
import { PUBLIC_URL } from "../config";
import { Link } from "react-router-dom";
import "../styles/ModernCarousel.css";

class ModernCarousel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentSlide: 0,
      isPlaying: true,
      isPaused: false,
      touchStartX: 0,
      touchEndX: 0,
      isTransitioning: false,
    };

    this.carouselRef = React.createRef();
    this.autoPlayInterval = null;
  }

  componentDidMount() {
    this.startAutoPlay();
    this.addEventListeners();
  }

  componentDidUpdate(prevProps) {
    // Handle dynamic slider count changes
    if (prevProps.sliders && this.props.sliders) {
      const prevCount = prevProps.sliders.length;
      const currentCount = this.props.sliders.length;

      // If slider count changed, update event listeners and autoplay
      if (prevCount !== currentCount) {
        this.removeEventListeners();
        this.stopAutoPlay();

        // Reset to first slide if current slide is out of bounds
        if (this.state.currentSlide >= currentCount) {
          this.setState({ currentSlide: 0 });
        }

        this.addEventListeners();
        this.startAutoPlay();
      }
    }
  }

  componentWillUnmount() {
    this.stopAutoPlay();
    this.removeEventListeners();
  }

  startAutoPlay = () => {
    const { sliders } = this.props;
    // Don't start autoplay for single slides
    if (!sliders || sliders.length <= 1) return;

    if (this.state.isPlaying && !this.state.isPaused) {
      this.autoPlayInterval = setInterval(() => {
        this.nextSlide();
      }, 5000); // 5 seconds
    }
  };

  stopAutoPlay = () => {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
      this.autoPlayInterval = null;
    }
  };

  addEventListeners = () => {
    if (this.carouselRef.current) {
      const { sliders } = this.props;

      // Add mouse events for autoplay control only if multiple slides
      if (sliders && sliders.length > 1) {
        this.carouselRef.current.addEventListener(
          "mouseenter",
          this.handleMouseEnter
        );
        this.carouselRef.current.addEventListener(
          "mouseleave",
          this.handleMouseLeave
        );
      }

      // Add touch events only if multiple slides for swipe functionality
      if (sliders && sliders.length > 1) {
        this.carouselRef.current.addEventListener(
          "touchstart",
          this.handleTouchStart
        );
        this.carouselRef.current.addEventListener(
          "touchend",
          this.handleTouchEnd
        );
      }
    }
  };

  removeEventListeners = () => {
    if (this.carouselRef.current) {
      const { sliders } = this.props;

      // Remove mouse events only if they were added
      if (sliders && sliders.length > 1) {
        this.carouselRef.current.removeEventListener(
          "mouseenter",
          this.handleMouseEnter
        );
        this.carouselRef.current.removeEventListener(
          "mouseleave",
          this.handleMouseLeave
        );
      }

      // Remove touch events only if they were added
      if (sliders && sliders.length > 1) {
        this.carouselRef.current.removeEventListener(
          "touchstart",
          this.handleTouchStart
        );
        this.carouselRef.current.removeEventListener(
          "touchend",
          this.handleTouchEnd
        );
      }
    }
  };

  handleMouseEnter = () => {
    this.setState({ isPaused: true });
    this.stopAutoPlay();
  };

  handleMouseLeave = () => {
    this.setState({ isPaused: false });
    this.startAutoPlay();
  };

  handleTouchStart = (e) => {
    this.setState({ touchStartX: e.changedTouches[0].screenX });
  };

  handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].screenX;
    this.setState({ touchEndX });
    this.handleSwipe(this.state.touchStartX, touchEndX);
  };

  handleSwipe = (startX, endX) => {
    const threshold = 50;
    const diff = startX - endX;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        this.nextSlide();
      } else {
        this.prevSlide();
      }
    }
  };

  nextSlide = () => {
    if (this.state.isTransitioning) return;

    const { sliders } = this.props;
    if (!sliders || sliders.length <= 1) return; // Don't navigate with single slide

    this.setState({ isTransitioning: true });

    const nextIndex = (this.state.currentSlide + 1) % sliders.length;
    this.setState({ currentSlide: nextIndex });

    setTimeout(() => {
      this.setState({ isTransitioning: false });
    }, 600);
  };

  prevSlide = () => {
    if (this.state.isTransitioning) return;

    const { sliders } = this.props;
    if (!sliders || sliders.length <= 1) return; // Don't navigate with single slide

    this.setState({ isTransitioning: true });

    const prevIndex =
      this.state.currentSlide === 0
        ? sliders.length - 1
        : this.state.currentSlide - 1;
    this.setState({ currentSlide: prevIndex });

    setTimeout(() => {
      this.setState({ isTransitioning: false });
    }, 600);
  };

  goToSlide = (index) => {
    if (this.state.isTransitioning || index === this.state.currentSlide) return;

    const { sliders } = this.props;
    if (!sliders || sliders.length <= 1) return; // Don't navigate with single slide

    this.setState({ isTransitioning: true, currentSlide: index });

    setTimeout(() => {
      this.setState({ isTransitioning: false });
    }, 600);
  };

  togglePlayPause = () => {
    const { sliders } = this.props;
    // Don't allow play/pause for single slides
    if (!sliders || sliders.length <= 1) return;

    const newIsPlaying = !this.state.isPlaying;
    this.setState({ isPlaying: newIsPlaying });

    if (newIsPlaying && !this.state.isPaused) {
      this.startAutoPlay();
    } else {
      this.stopAutoPlay();
    }
  };

  render() {
    const { sliders } = this.props;
    if (!sliders || sliders.length === 0) return null;

    const { currentSlide, isPlaying, isTransitioning } = this.state;
    const isSingleSlider = sliders.length === 1;

    return (
      <div
        className={`modern-carousel-container ${
          isSingleSlider ? "single-slider" : ""
        }`}
      >
        <div
          className={`modern-carousel ${isSingleSlider ? "single-slider" : ""}`}
          ref={this.carouselRef}
          role="region"
          aria-label={isSingleSlider ? "Image" : "Image Carousel"}
        >
          {/* Slides */}
          <div className="carousel-slides">
            {sliders.map((slide, index) => {
              const { image, title, content, link, id, slug, products } = slide;
              const isActive = index === currentSlide;

              // Slider'a ürün bağlıysa /slider/:slug (yoksa :id), değilse link.url'e gider
              const hasProducts = products && products.length > 0;
              const slideLink = hasProducts
                ? `/slider/${slug || id}`
                : link && link.url
                ? link.url
                : null;

              // Overlay yalnızca üzerinde okunması gereken metin varsa gösterilir;
              // metinsiz (görsele gömülü tasarım) slider'lar soluk görünmesin diye kaldırılır.
              const hasOverlayText =
                (title && title.text) || (content && content.text);
              const imageBlock = (
                <div className="slide-image-container">
                  <img
                    src={PUBLIC_URL(image)}
                    alt={(title && title.text) || "Slider Image"}
                    className="slide-image"
                    loading={index === 0 ? "eager" : "lazy"}
                  />
                  {hasOverlayText && <div className="slide-overlay"></div>}
                </div>
              );

              // Skip prev/next calculations for single sliders
              const isPrev =
                sliders.length > 1
                  ? index ===
                    (currentSlide === 0 ? sliders.length - 1 : currentSlide - 1)
                  : false;
              const isNext =
                sliders.length > 1
                  ? index === (currentSlide + 1) % sliders.length
                  : false;

              return (
                <div
                  key={index}
                  className={`carousel-slide ${isActive ? "active" : ""} ${
                    isPrev ? "prev" : ""
                  } ${isNext ? "next" : ""} ${
                    isTransitioning ? "transitioning" : ""
                  }`}
                  aria-hidden={!isActive}
                >
                  {slideLink ? (
                    <Link
                      to={slideLink}
                      className="slide-image-link"
                      aria-label={(title && title.text) || "Slider"}
                    >
                      {imageBlock}
                    </Link>
                  ) : (
                    imageBlock
                  )}

                  {((title && title.text) ||
                    (content && content.text) ||
                    (link && link.text && slideLink)) && (
                    <div className="slide-content">
                      <div className="content-wrapper">
                        {title && title.text && (
                          <h2
                            className="slide-title"
                            style={{ color: title.color || "#FFFFFF" }}
                          >
                            {title.text}
                          </h2>
                        )}

                        {content && content.text && (
                          <p
                            className="slide-description"
                            style={{ color: content.color || "#FFFFFF" }}
                          >
                            {content.text}
                          </p>
                        )}

                        {link && link.text && slideLink && (
                          <Link
                            to={slideLink}
                            className="slide-cta-button"
                            style={{
                              backgroundColor: link.bg || "#66cc33",
                              color: link.color || "#FFFFFF",
                            }}
                          >
                            {link.text}
                            <span className="button-arrow">→</span>
                          </Link>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Navigation Controls - Only show if multiple slides */}
          {sliders.length > 1 && (
            <React.Fragment>
              <button
                className="carousel-nav-button prev-button"
                onClick={this.prevSlide}
                disabled={isTransitioning}
                aria-label="Previous slide"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                </svg>
              </button>

              <button
                className="carousel-nav-button next-button"
                onClick={this.nextSlide}
                disabled={isTransitioning}
                aria-label="Next slide"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                </svg>
              </button>
            </React.Fragment>
          )}

          {/* Indicators - Only show if multiple slides */}
          {sliders.length > 1 && (
            <div className="carousel-indicators">
              {sliders.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${
                    index === currentSlide ? "active" : ""
                  }`}
                  onClick={() => this.goToSlide(index)}
                  disabled={isTransitioning}
                  aria-label={`Go to slide ${index + 1}`}
                >
                  <span className="indicator-progress">
                    <span
                      className="progress-fill"
                      style={{
                        animationDuration:
                          index === currentSlide && isPlaying ? "5s" : "0s",
                        animationPlayState:
                          index === currentSlide &&
                          isPlaying &&
                          !this.state.isPaused
                            ? "running"
                            : "paused",
                      }}
                    ></span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Play/Pause Control - Only show if multiple slides */}
          {sliders.length > 1 && (
            <button
              className="carousel-play-pause"
              onClick={this.togglePlayPause}
              aria-label={isPlaying ? "Pause carousel" : "Play carousel"}
            >
              {isPlaying ? (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>
    );
  }
}

ModernCarousel.propTypes = {
  sliders: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string.isRequired,
      title: PropTypes.shape({
        text: PropTypes.string,
        color: PropTypes.string,
      }),
      content: PropTypes.shape({
        text: PropTypes.string,
        color: PropTypes.string,
      }),
      link: PropTypes.shape({
        text: PropTypes.string,
        url: PropTypes.string,
        color: PropTypes.string,
        bg: PropTypes.string,
      }),
    })
  ),
};

ModernCarousel.defaultProps = {
  sliders: [],
};

export default ModernCarousel;



// WEBPACK FOOTER //
// ./src/components/Carousel.js